import React, { useState, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

interface BirthDatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  label?: string;
  className?: string;
}

const BirthDatePicker: React.FC<BirthDatePickerProps> = ({ value, onChange, label, className }) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [manualValue, setManualValue] = useState('');

  // Sync internal state with external value
  useEffect(() => {
    if (value && value.includes('-')) {
      const [y, m, d] = value.split('-');
      setYear(y);
      setMonth(m.padStart(2, '0'));
      setDay(d.padStart(2, '0'));
      setManualValue(`${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`);
    } else {
      setYear('');
      setMonth('');
      setDay('');
      setManualValue('');
    }
  }, [value]);

  const handleSelectChange = (newDay: string, newMonth: string, newYear: string) => {
    setDay(newDay);
    setMonth(newMonth);
    setYear(newYear);

    if (newDay && newMonth && newYear) {
      const formattedDate = `${newYear}-${newMonth.padStart(2, '0')}-${newDay.padStart(2, '0')}`;
      onChange(formattedDate);
    }
  };

  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    // Allow only numbers and slashes
    val = val.replace(/[^0-9/]/g, '');
    
    // Auto-format DD/MM/YYYY
    if (val.length === 2 && !val.includes('/')) val += '/';
    if (val.length === 5 && val.split('/').length === 2) val += '/';
    if (val.length > 10) val = val.substring(0, 10);
    
    setManualValue(val);

    // If valid format, update external value
    const parts = val.split('/');
    if (parts.length === 3 && parts[2].length === 4) {
      const d = parts[0].padStart(2, '0');
      const m = parts[1].padStart(2, '0');
      const y = parts[2];
      const formattedDate = `${y}-${m}-${d}`;
      // Basic validation
      const date = new Date(formattedDate);
      if (!isNaN(date.getTime())) {
        onChange(formattedDate);
      }
    }
  };

  const years = Array.from({ length: 110 }, (_, i) => (new Date().getFullYear() - i).toString());
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  return (
    <div className={`space-y-2 ${className}`} dir="rtl">
      {label && <label className="text-sm font-bold text-slate-700 mr-2">{label}</label>}
      
      <div className="flex flex-col gap-3">
        {/* Manual Entry */}
        <div className="relative">
          <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            value={manualValue}
            onChange={handleManualChange}
            placeholder="DD/MM/YYYY"
            className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-mono"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-bold">
            הקלדה ידנית
          </div>
        </div>

        {/* Dropdown Selects */}
        <div className="grid grid-cols-3 gap-2">
          <div className="relative">
            <select
              value={day}
              onChange={(e) => handleSelectChange(e.target.value, month, year)}
              className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 outline-none appearance-none"
            >
              <option value="">יום</option>
              {days.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <ChevronDown className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
          </div>

          <div className="relative">
            <select
              value={month}
              onChange={(e) => handleSelectChange(day, e.target.value, year)}
              className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 outline-none appearance-none"
            >
              <option value="">חודש</option>
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <ChevronDown className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
          </div>

          <div className="relative">
            <select
              value={year}
              onChange={(e) => handleSelectChange(day, month, e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 outline-none appearance-none"
            >
              <option value="">שנה</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <ChevronDown className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthDatePicker;
